import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { SiteHeader } from '../components/SiteLayout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Storefront, CurrencyDollar, ChartLineUp, ShieldCheck, Key, FileText,
  ClockCounterClockwise, CheckCircle, XCircle, Lightning,
} from '@phosphor-icons/react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area } from 'recharts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Counter({ label, value, sub, color }) {
  return (
    <div className="card-flat p-3 bg-white">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">{label}</p>
      <p className={`text-2xl font-black tabular-nums ${color || 'text-[#111827]'}`}>{value ?? 0}</p>
      {sub && <p className="text-[10px] text-[#6B7280] mt-0.5">{sub}</p>}
    </div>
  );
}

function SubActionDialog({ open, onOpenChange, sub, onDone }) {
  const [action, setAction] = useState('approve');
  const [duration, setDuration] = useState(30);
  const [quotePrice, setQuotePrice] = useState('');
  const [quotaOverride, setQuotaOverride] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open && sub) {
      setAction(sub.status === 'quote_requested' ? 'quote' : 'approve');
      setDuration(sub.billing_period === 'annual' ? 365 : 30);
      setQuotePrice(sub.price_usd || '');
      setQuotaOverride(''); setReason(''); setNotes(''); setErr('');
    }
  }, [open, sub]);

  const submit = async () => {
    setSaving(true); setErr('');
    const body = { action, notes };
    if (action === 'approve' || action === 'activate' || action === 'extend') body.duration_days = Number(duration);
    if (action === 'quote') body.quote_price_usd = Number(quotePrice);
    if (quotaOverride) body.quota_override = Number(quotaOverride);
    if (action === 'reject') body.rejection_reason = reason;
    try {
      await axios.post(`${API_URL}/api/marketplace/admin/subscriptions/${sub.id}/action`, body, { withCredentials: true });
      toast.success(`Subscription ${action}d`);
      onDone(); onOpenChange(false);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  if (!sub) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-sub-action-dialog">
        <DialogHeader>
          <DialogTitle>Manage subscription — {sub.ref}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-[12px] text-[#4B5563]">
            <strong>{sub.plan_name}</strong> · {sub.billing_period} · {sub.user_email} ({sub.company || '—'})
          </p>
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">Action</Label>
            <select value={action} onChange={(e) => setAction(e.target.value)} className="mt-1 w-full h-9 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="admin-sub-action">
              <option value="approve">Approve & activate</option>
              <option value="quote">Send quote (enterprise)</option>
              <option value="reject">Reject</option>
              <option value="cancel">Cancel</option>
              <option value="extend">Extend</option>
            </select>
          </div>
          {(action === 'approve' || action === 'extend') && (
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">Duration (days)</Label>
              <Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} className="mt-1 h-9 text-sm" data-testid="admin-sub-duration" />
            </div>
          )}
          {action === 'approve' && (
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">Quota override (optional)</Label>
              <Input type="number" value={quotaOverride} onChange={(e) => setQuotaOverride(e.target.value)} className="mt-1 h-9 text-sm" placeholder={sub.quota_lookups_per_month?.toString()} data-testid="admin-sub-quota" />
            </div>
          )}
          {action === 'quote' && (
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">Quote price (USD)</Label>
              <Input type="number" value={quotePrice} onChange={(e) => setQuotePrice(e.target.value)} className="mt-1 h-9 text-sm" data-testid="admin-sub-quote-price" />
            </div>
          )}
          {action === 'reject' && (
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">Rejection reason</Label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className="text-sm" data-testid="admin-sub-reason" />
            </div>
          )}
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="text-sm" data-testid="admin-sub-notes" />
          </div>
          {err && <p className="text-[12px] text-[#9B1C1C]" data-testid="admin-sub-error">{err}</p>}
          <Button onClick={submit} disabled={saving} className="w-full h-10 rounded bg-[#FF4E6A] hover:bg-[#E04057] text-white font-semibold" data-testid="admin-sub-submit">
            {saving ? 'Submitting…' : `Confirm — ${action}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PurchaseActionDialog({ open, onOpenChange, purchase, onDone }) {
  const [action, setAction] = useState('approve_and_generate');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open && purchase) {
      setAction(purchase.status === 'delivered' ? 'regenerate' : 'approve_and_generate');
      setNotes(''); setErr('');
    }
  }, [open, purchase]);

  const submit = async () => {
    setSaving(true); setErr('');
    try {
      await axios.post(`${API_URL}/api/marketplace/admin/purchases/${purchase.id}/action`,
        { action, notes }, { withCredentials: true });
      toast.success(`Purchase ${action.replace(/_/g, ' ')}d`);
      onDone(); onOpenChange(false);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  if (!purchase) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-purchase-action-dialog">
        <DialogHeader>
          <DialogTitle>Manage report purchase — {purchase.ref}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-[12px] text-[#4B5563]">
            <strong>{purchase.tier_name}</strong> · {purchase.item_type}: <em>{purchase.item_label}</em><br/>
            {purchase.user_email} ({purchase.company || '—'}) · ${purchase.price_usd}
          </p>
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">Action</Label>
            <select value={action} onChange={(e) => setAction(e.target.value)} className="mt-1 w-full h-9 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="admin-purchase-action">
              <option value="approve_and_generate">Approve & generate PDF</option>
              <option value="regenerate">Re-generate PDF</option>
              <option value="reject">Reject</option>
            </select>
          </div>
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">Notes / rejection reason</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="text-sm" data-testid="admin-purchase-notes" />
          </div>
          {err && <p className="text-[12px] text-[#9B1C1C]" data-testid="admin-purchase-error">{err}</p>}
          <Button onClick={submit} disabled={saving} className="w-full h-10 rounded bg-[#FF4E6A] hover:bg-[#E04057] text-white font-semibold" data-testid="admin-purchase-submit">
            {saving ? 'Submitting…' : `Confirm`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MarketplaceAdminPage() {
  // Wait for AuthContext to finish its /auth/refresh handshake before firing
  // the five admin-only GETs. Without this guard, page load races the auth
  // refresh and any 401 surfaces as React's unhandled-error overlay.
  const { isAdmin, loading: authLoading } = useAuth();
  const ready = !authLoading && isAdmin;

  const [tab, setTab] = useState('subscriptions');
  const [subs, setSubs] = useState([]);
  const [subCounters, setSubCounters] = useState({});
  const [subStatus, setSubStatus] = useState('');
  const [purchases, setPurchases] = useState([]);
  const [purchaseCounters, setPurchaseCounters] = useState({});
  const [purchaseStatus, setPurchaseStatus] = useState('');
  const [tokens, setTokens] = useState([]);
  const [audit, setAudit] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const [selSub, setSelSub] = useState(null);
  const [selPurchase, setSelPurchase] = useState(null);

  // Centralised error-swallowing wrapper — surface a toast on real failures,
  // ignore the transient 401 that fires before auth refresh completes.
  const safeGet = useCallback(async (url, label) => {
    try {
      return await axios.get(url, { withCredentials: true });
    } catch (e) {
      const status = e?.response?.status;
      // Silent on 401/403 — caller is gated on `ready`, so any 401 is a
      // race that resolves on the next render once the cookie is set.
      if (status !== 401 && status !== 403) {
        toast.error(`${label}: ${e?.response?.data?.detail || e.message}`);
      }
      return null;
    }
  }, []);

  const loadSubs = useCallback(async () => {
    if (!ready) return;
    const q = new URLSearchParams();
    if (subStatus) q.set('status', subStatus);
    q.set('limit', '100');
    const r = await safeGet(`${API_URL}/api/marketplace/admin/subscriptions?${q}`, 'Subscriptions');
    if (r) { setSubs(r.data.items || []); setSubCounters(r.data.counters || {}); }
  }, [ready, subStatus, safeGet]);

  const loadPurchases = useCallback(async () => {
    if (!ready) return;
    const q = new URLSearchParams();
    if (purchaseStatus) q.set('status', purchaseStatus);
    q.set('limit', '100');
    const r = await safeGet(`${API_URL}/api/marketplace/admin/purchases?${q}`, 'Purchases');
    if (r) { setPurchases(r.data.items || []); setPurchaseCounters(r.data.counters || {}); }
  }, [ready, purchaseStatus, safeGet]);

  const loadTokens = useCallback(async () => {
    if (!ready) return;
    const r = await safeGet(`${API_URL}/api/marketplace/admin/api-tokens`, 'API tokens');
    if (r) setTokens(r.data.items || []);
  }, [ready, safeGet]);

  const loadAudit = useCallback(async () => {
    if (!ready) return;
    const r = await safeGet(`${API_URL}/api/marketplace/admin/audit?limit=80`, 'Audit feed');
    if (r) setAudit(r.data.items || []);
  }, [ready, safeGet]);

  const loadAnalytics = useCallback(async () => {
    if (!ready) return;
    const r = await safeGet(`${API_URL}/api/marketplace/admin/analytics`, 'Analytics');
    if (r) setAnalytics(r.data);
  }, [ready, safeGet]);

  useEffect(() => { loadSubs(); }, [loadSubs]);
  useEffect(() => { loadPurchases(); }, [loadPurchases]);
  useEffect(() => { loadTokens(); loadAudit(); loadAnalytics(); }, [loadTokens, loadAudit, loadAnalytics]);

  const revokeToken = async (id) => {
    if (!window.confirm('Revoke this token? Integration will fail immediately.')) return;
    try {
      await axios.post(`${API_URL}/api/marketplace/admin/api-tokens/${id}/revoke`, {}, { withCredentials: true });
      toast.success('Token revoked'); loadTokens();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Storefront size={22} className="text-[#FF4E6A]" weight="duotone" />
            <h1 className="text-2xl font-bold tracking-tight text-[#111827]">Marketplace Admin Console</h1>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] bg-[#F3F4F6] px-2 py-0.5 rounded ml-2">licensing · approvals · analytics</span>
          </div>
          <p className="text-sm text-[#4B5563]">Approve subscription requests, process individual report purchases, manage enterprise API tokens, and review revenue & usage analytics.</p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <Link to="/assessor"><Button variant="outline" className="h-8 text-xs rounded">← Assessor Console</Button></Link>
            <Link to="/marketplace"><Button variant="outline" className="h-8 text-xs rounded" data-testid="admin-to-public">View public catalog →</Button></Link>
          </div>
        </div>

        {/* Analytics counters */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="admin-analytics-strip">
            <Counter label="MRR (active)"     value={`$${analytics.revenue?.mrr_usd?.toLocaleString() || 0}`}     sub="active monthly subs" color="text-[#10B981]" />
            <Counter label="ARR (run-rate)"   value={`$${analytics.revenue?.arr_usd?.toLocaleString() || 0}`}     sub="incl. annual subs" />
            <Counter label="One-time"         value={`$${analytics.revenue?.one_time_total_usd?.toLocaleString() || 0}`} sub="delivered reports" />
            <Counter label="Active subs"      value={analytics.subscriptions?.active}      sub={`${analytics.subscriptions?.pending_approval || 0} pending`} />
            <Counter label="Pending reports"  value={analytics.purchases?.pending_review}  sub="Aᴳ review queue" color="text-[#FF4E6A]" />
            <Counter label="Active tokens"    value={analytics.api?.active_tokens}         sub={`${analytics.api?.total_tokens || 0} total`} />
            <Counter label="API calls 30d"    value={analytics.api?.total_calls_30d?.toLocaleString()} sub="last 30 days" />
          </div>
        )}

        {/* Revenue & API charts */}
        {analytics && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="card-flat p-4 bg-white" data-testid="admin-chart-revenue">
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-3">Revenue composition</p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={[
                  { name: 'MRR (monthly)', value: analytics.revenue?.mrr_usd || 0, fill: '#10B981' },
                  { name: 'ARR run-rate',  value: analytics.revenue?.arr_usd || 0, fill: '#0EA5E9' },
                  { name: 'One-time',      value: analytics.revenue?.one_time_total_usd || 0, fill: '#FF4E6A' },
                ]} layout="vertical" margin={{ left: 12, right: 16 }}>
                  <CartesianGrid stroke="#E5E7EB" horizontal={false} />
                  <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={110} />
                  <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card-flat p-4 bg-white" data-testid="admin-chart-api-consumers">
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-3">Top API consumers (calls)</p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={(analytics.top_api_consumers || []).slice(0, 8).map((t) => ({
                  name: t.user_email?.split('@')[0] || t.label || 'token', value: t.total_calls || 0,
                }))}>
                  <CartesianGrid stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#0B1426" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-5 border-b border-gray-200">
          {[
            { k: 'subscriptions', label: 'Subscriptions', count: subCounters.total },
            { k: 'purchases',     label: 'Reports',       count: purchaseCounters.total },
            { k: 'tokens',        label: 'API tokens',    count: tokens.length },
            { k: 'audit',         label: 'Audit',         count: audit.length },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 ${tab === t.k ? 'border-[#FF4E6A] text-[#111827]' : 'border-transparent text-[#4B5563] hover:text-[#111827]'}`}
              data-testid={`admin-tab-${t.k}`}>
              {t.label} <span className="text-[10px] text-[#9CA3AF] ml-1">{t.count || 0}</span>
            </button>
          ))}
        </div>

        {tab === 'subscriptions' && (
          <div data-testid="admin-subs-pane">
            <div className="flex gap-2 mb-3 flex-wrap">
              {['', 'pending_approval', 'quote_requested', 'active', 'rejected', 'expired'].map(s => (
                <button key={s} onClick={() => setSubStatus(s)}
                  className={`text-xs px-2.5 py-1.5 rounded border ${subStatus === s ? 'bg-[#0B1426] text-white border-[#0B1426]' : 'bg-white border-gray-300 text-[#4B5563]'}`}
                  data-testid={`admin-sub-filter-${s || 'all'}`}>
                  {s || 'All'} <span className="text-[10px] ml-1">{s ? subCounters[s] || 0 : subCounters.total || 0}</span>
                </button>
              ))}
            </div>
            <div className="space-y-2">
              {subs.length === 0 ? <p className="text-sm text-[#4B5563] py-10 text-center">No subscriptions match filter.</p> :
                subs.map(s => (
                  <div key={s.id} className="card-flat p-4 bg-white flex items-center justify-between gap-3" data-testid={`admin-sub-row-${s.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-bold text-[#111827]">{s.plan_name}</p>
                        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-gray-100 rounded">{s.status}</span>
                        <span className="text-[10px] text-[#6B7280]">{s.ref}</span>
                      </div>
                      <p className="text-[12px] text-[#4B5563] mt-0.5">
                        {s.user_email} · {s.company || '—'} · {s.billing_period} · ${s.price_usd?.toLocaleString() || 0}
                      </p>
                      {s.use_case && <p className="text-[11px] text-[#6B7280] italic mt-1 line-clamp-1">{s.use_case}</p>}
                    </div>
                    <Button onClick={() => setSelSub(s)} className="h-8 text-xs rounded bg-[#0B1426] hover:bg-[#1B2538] text-white" data-testid={`admin-sub-manage-${s.id}`}>
                      Manage →
                    </Button>
                  </div>
                ))
              }
            </div>
          </div>
        )}

        {tab === 'purchases' && (
          <div data-testid="admin-purchases-pane">
            <div className="flex gap-2 mb-3 flex-wrap">
              {['', 'pending_review', 'auto_generating', 'delivered', 'rejected', 'generation_failed'].map(s => (
                <button key={s} onClick={() => setPurchaseStatus(s)}
                  className={`text-xs px-2.5 py-1.5 rounded border ${purchaseStatus === s ? 'bg-[#0B1426] text-white border-[#0B1426]' : 'bg-white border-gray-300 text-[#4B5563]'}`}
                  data-testid={`admin-purchase-filter-${s || 'all'}`}>
                  {s || 'All'} <span className="text-[10px] ml-1">{s ? purchaseCounters[s] || 0 : purchaseCounters.total || 0}</span>
                </button>
              ))}
            </div>
            <div className="space-y-2">
              {purchases.length === 0 ? <p className="text-sm text-[#4B5563] py-10 text-center">No purchases match filter.</p> :
                purchases.map(p => (
                  <div key={p.id} className="card-flat p-4 bg-white flex items-center justify-between gap-3" data-testid={`admin-purchase-row-${p.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-bold text-[#111827]">{p.item_label}</p>
                        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-gray-100 rounded">{p.status}</span>
                        {p.admin_review_required && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 bg-[#FDE8E8] text-[#9B1C1C] border border-[#F8B4B4] rounded">review</span>}
                      </div>
                      <p className="text-[12px] text-[#4B5563] mt-0.5">{p.tier_name} · {p.user_email} · ${p.price_usd}</p>
                    </div>
                    <Button onClick={() => setSelPurchase(p)} className="h-8 text-xs rounded bg-[#FF4E6A] hover:bg-[#E04057] text-white" data-testid={`admin-purchase-manage-${p.id}`}>
                      Process →
                    </Button>
                  </div>
                ))
              }
            </div>
          </div>
        )}

        {tab === 'tokens' && (
          <div className="space-y-2" data-testid="admin-tokens-pane">
            {tokens.length === 0 ? <p className="text-sm text-[#4B5563] py-10 text-center">No tokens issued.</p> :
              tokens.map(t => (
                <div key={t.id} className="card-flat p-3 bg-white flex items-center justify-between gap-3" data-testid={`admin-token-row-${t.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-[#111827]">{t.label || 'Token'} · <span className="text-[10px] font-mono bg-gray-100 px-1.5 py-0.5 rounded">{t.token_prefix}…</span></p>
                    <p className="text-[11px] text-[#4B5563]">{t.user_email} ({t.company}) · {t.plan_code} · {t.total_calls?.toLocaleString() || 0} calls · {t.revoked_at ? <span className="text-[#9B1C1C]">revoked</span> : 'active'}</p>
                  </div>
                  {!t.revoked_at && (
                    <Button onClick={() => revokeToken(t.id)} variant="outline" className="h-8 text-xs rounded text-[#9B1C1C] border-[#F8B4B4] hover:bg-[#FDE8E8]" data-testid={`admin-token-revoke-${t.id}`}>
                      Revoke
                    </Button>
                  )}
                </div>
              ))
            }
          </div>
        )}

        {tab === 'audit' && (
          <div className="card-flat bg-white max-h-[600px] overflow-y-auto divide-y divide-gray-100" data-testid="admin-audit-pane">
            {audit.length === 0 ? <p className="text-sm text-[#4B5563] py-10 text-center">No audit entries yet.</p> :
              audit.map(a => (
                <div key={a.id} className="px-4 py-2.5 flex items-center gap-3 text-[12px]" data-testid={`admin-audit-row-${a.id}`}>
                  <span className="font-mono bg-gray-100 text-[10px] px-1.5 py-0.5 rounded">{a.action}</span>
                  <span className="flex-1 text-[#4B5563] truncate">
                    {a.subscription_id && <>sub: <code>{a.subscription_id.slice(-8)}</code></>}
                    {a.purchase_id && <>purchase: <code>{a.purchase_id.slice(-8)}</code></>}
                    {a.token_id && <>token: <code>{a.token_id.slice(-8)}</code></>}
                    {a.by && <> · by {a.by}</>}
                    {a.user_id && <> · user {a.user_id.slice(-8)}</>}
                  </span>
                  <span className="text-[10px] text-[#9CA3AF] tabular-nums">{new Date(a.at).toLocaleString()}</span>
                </div>
              ))
            }
          </div>
        )}

        {/* Top consumers strip */}
        {analytics?.top_api_consumers?.length > 0 && (
          <div className="mt-8 card-flat bg-white p-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-3 flex items-center gap-1"><Lightning size={11} weight="fill" /> Top API consumers (30d)</p>
            <div className="space-y-1.5">
              {analytics.top_api_consumers.map((t) => (
                <div key={t.token_id} className="flex items-center justify-between text-[12px] py-1 border-b border-gray-100 last:border-0">
                  <span className="truncate">{t.user_email} · <span className="text-[#6B7280]">{t.label}</span></span>
                  <span className="font-bold tabular-nums text-[#0B1426]">{t.total_calls?.toLocaleString() || 0}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <SubActionDialog open={!!selSub} onOpenChange={() => setSelSub(null)} sub={selSub} onDone={loadSubs} />
      <PurchaseActionDialog open={!!selPurchase} onOpenChange={() => setSelPurchase(null)} purchase={selPurchase} onDone={loadPurchases} />
    </div>
  );
}
